import { useState, useEffect, useCallback, useRef } from "react";
import {
  checkIsBlockedAccess,
  getBlockPageSettings,
} from "../api/userBlockedAccess";
import {
  clearPreAuthBlockedPage,
  clearSession,
  getPreAuthBlockedPage,
  getSession,
  setPreAuthBlockedPage,
} from "../api/sessionManagement";
import { checkSession } from "../api/sessionCheck";
import {
  probeAuthAvailability,
  probeCoreBackendAvailability,
} from "../api/serviceAvailability";

const BLOCK_ACCESS_CHECK_INTERVAL_MS = 5000;
const MAINTENANCE_FAILURE_THRESHOLD = 2;
interface UseBlockedAccessReturn {
  isBlocked: boolean;
  isMaintenance: boolean;
  blockPageText: string | null;
  blockEndDate: string | null;
  isLoading: boolean;
  checkBlockedAccess: () => Promise<void>;
}

export const useBlockedAccess = (): UseBlockedAccessReturn => {
  const [isBlocked, setIsBlocked] = useState<boolean>(false);
  const [isMaintenance, setIsMaintenance] = useState<boolean>(false);
  const [blockPageText, setBlockPageText] = useState<string | null>(null);
  const [blockEndDate, setBlockEndDate] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const isInitialLoadRef = useRef(true);
  const isCheckingRef = useRef(false);
  const consecutiveMaintenanceFailuresRef = useRef(0);

  const resetBlockedState = useCallback(() => {
    setIsBlocked(false);
    setBlockPageText(null);
    setBlockEndDate(null);
  }, []);

  const enableMaintenanceMode = useCallback(() => {
    setIsMaintenance(true);
    resetBlockedState();
  }, [resetBlockedState]);

  const disableMaintenanceMode = useCallback(() => {
    setIsMaintenance(false);
  }, []);

  const finishInitialLoad = useCallback(() => {
    if (!isInitialLoadRef.current) {
      return;
    }

    setIsLoading(false);
    isInitialLoadRef.current = false;
  }, []);

  const registerSuccessfulCheck = useCallback(() => {
    consecutiveMaintenanceFailuresRef.current = 0;
    disableMaintenanceMode();
  }, [disableMaintenanceMode]);

  const handlePotentialMaintenanceFailure = useCallback(() => {
    consecutiveMaintenanceFailuresRef.current += 1;

    if (
      isInitialLoadRef.current ||
      consecutiveMaintenanceFailuresRef.current >= MAINTENANCE_FAILURE_THRESHOLD
    ) {
      enableMaintenanceMode();
    }
  }, [enableMaintenanceMode]);

  const checkBlockedAccess = useCallback(async () => {
    if (isCheckingRef.current) {
      return;
    }

    isCheckingRef.current = true;

    const sessionId = getSession();

    if (isInitialLoadRef.current) {
      setIsLoading(true);
    }

    try {
      const [coreBackendProbe, authProbe] = await Promise.all([
        probeCoreBackendAvailability(),
        sessionId ? Promise.resolve(null) : probeAuthAvailability(),
      ]);

      const isCoreUnavailable = !coreBackendProbe.isAvailable;
      const isAuthUnavailable = Boolean(authProbe && !authProbe.isAvailable);

      if (isCoreUnavailable || isAuthUnavailable) {
        handlePotentialMaintenanceFailure();
        return;
      }

      registerSuccessfulCheck();

      if (!sessionId) {
        const preAuthBlocked = getPreAuthBlockedPage();
        if (preAuthBlocked?.text) {
          setIsBlocked(true);
          setBlockPageText(preAuthBlocked.text);
          setBlockEndDate(preAuthBlocked.endDate || null);
        } else {
          resetBlockedState();
        }
        return;
      }

      const sessionCheckResponse = await checkSession();
      if (sessionCheckResponse.error) {
        const status = (sessionCheckResponse.error as any)?.response?.status;
        if ([500, 502, 503, 504].includes(status) || !status) {
          handlePotentialMaintenanceFailure();
          return;
        }

        clearSession();
        resetBlockedState();
        return;
      }

      const [settingsResponse, blockedResponse] = await Promise.all([
        getBlockPageSettings(),
        checkIsBlockedAccess(),
      ]);

      const settingsStatus = (settingsResponse.error as any)?.response?.status;
      const blockedStatus = (blockedResponse.error as any)?.response?.status;
      const hasServiceUnavailable = [settingsStatus, blockedStatus].some((status) =>
        [500, 502, 503, 504].includes(status)
      );

      if (hasServiceUnavailable) {
        handlePotentialMaintenanceFailure();
        return;
      }

      if (settingsStatus === 401 || blockedStatus === 401) {
        clearSession();
        resetBlockedState();
        return;
      }

      if (settingsResponse.error || !settingsResponse.data) {
        const preAuthBlocked = getPreAuthBlockedPage();
        if (preAuthBlocked?.text) {
          setIsBlocked(true);
          setBlockPageText(preAuthBlocked.text);
          setBlockEndDate(preAuthBlocked.endDate || null);
        } else {
          resetBlockedState();
        }
        return;
      }

      const settings = settingsResponse.data?.data ?? [];

      if (!settings || settings.length === 0) {
        clearPreAuthBlockedPage();
        resetBlockedState();
        return;
      }

      const activePages = settings.filter(
        (page) => page.is_page_active === true
      );

      if (activePages.length === 0) {
        clearPreAuthBlockedPage();
        resetBlockedState();
        return;
      }

      const sortedPages = [...activePages].sort((a, b) => a.priority - b.priority);
      const isBlockedAccess = blockedResponse.error
        ? false
        : blockedResponse.data?.is_blocked_access ?? false;
      const selectedPage = sortedPages.find((page) => (
        page.priority !== 1 || isBlockedAccess
      ));

      if (!selectedPage) {
        clearPreAuthBlockedPage();
        resetBlockedState();
        return;
      }

      setIsBlocked(true);
      setBlockPageText(selectedPage.text);
      setBlockEndDate(selectedPage.end_date || null);
      setPreAuthBlockedPage(selectedPage.text, selectedPage.end_date || null);
    } catch (error) {
      handlePotentialMaintenanceFailure();
    } finally {
      isCheckingRef.current = false;
      finishInitialLoad();
    }
  }, [
    enableMaintenanceMode,
    finishInitialLoad,
    handlePotentialMaintenanceFailure,
    registerSuccessfulCheck,
    resetBlockedState,
  ]);

  useEffect(() => {
    void checkBlockedAccess();

    const interval = setInterval(() => {
      void checkBlockedAccess();
    }, BLOCK_ACCESS_CHECK_INTERVAL_MS);

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        void checkBlockedAccess();
      }
    };

    const handleWindowFocus = () => {
      void checkBlockedAccess();
    };

    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === "sessionId" || event.key === "preAuthBlockedPage") {
        void checkBlockedAccess();
      }
    };

    const handleSessionIdChange = () => {
      void checkBlockedAccess();
    };

    window.addEventListener("storage", handleStorageChange);
    window.addEventListener("sessionIdChanged", handleSessionIdChange);
    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("focus", handleWindowFocus);

    return () => {
      clearInterval(interval);
      window.removeEventListener("storage", handleStorageChange);
      window.removeEventListener("sessionIdChanged", handleSessionIdChange);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("focus", handleWindowFocus);
    };
  }, [checkBlockedAccess]);

  return {
    isBlocked,
    isMaintenance,
    blockPageText,
    blockEndDate,
    isLoading,
    checkBlockedAccess,
  };
};
